import { Consent, ChallengeJoinConsentUpdateDto, ChallengeStatus } from '@core/api/ChallengeClient';
import { autodisposer } from '@core/reactions';
import { injectTSDI } from '@core/tsdi';
import { FieldState, FormState } from 'formstate';
import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { component } from 'tsdi';
import { ChallengeDataStore } from '../data-store';
import { formErrors, isFormStateDirty, makeFormClean } from '@components/utils/form';
import { wrapInFormField } from '@core/forms';
import { requiredI18n } from '@components/utils/validators';

import { bind } from 'lodash-decorators';
import { StepStatus } from '../store';
import { I18NText } from '@api/CorporateClient';
import { I18n } from '@core/i18n';
import { wrapRequest } from 'mobx-wrap-request';
import { Api } from '@core/api';

export type ChallengeConsentsFormType = ReturnType<
    ChallengeConsentsStore['generateForm']
>;

export type ChallengeConsentFormType = FormState<{
    text: FieldState<I18NText>;
    required: FieldState<boolean>;
    needsCheckbox: FieldState<boolean>;
    showAfterAccept: FieldState<boolean>;
}>;

@component
export class ChallengeConsentsStore {
    constructor() {
        this.form = this.generateForm(
            this.challengeDataStore.liveChallengeConfiguration?.joinConsents || []
        );

        makeObservable(this, {
            form: observable,
            fields: computed,
            errors: computed,
            isActive: observable,
            state: observable,
            onCollapsedToggle: action.bound,
            openStep: action.bound,
            closeStep: action.bound,
            enableConsents: action.bound,
            disableConsents: action.bound
        });
    }

    public form: ChallengeConsentsFormType;
    public state: StepStatus = StepStatus.CREATING;
    public isActive = false;

    private get challengeDataStore() {
        return injectTSDI(ChallengeDataStore);
    }

    private generateForm(joinConsents: Consent[]) {
        return new FormState({
            consents: new FieldState<ChallengeConsentFormType[]>(
                joinConsents.map((rule) => this.createConsentForm(rule))
            )
        });
    }

    private createConsentForm(
        consent?: Consent
    ): ChallengeConsentFormType {
        return new FormState({
            text: new FieldState<I18NText>(
                consent?.text ||
                I18n.textToI18nTextConverter(
                    consent?.text
                )
            ).validators(requiredI18n()),
            required: new FieldState<boolean>(
                consent?.required !== undefined ? consent.required : true
            ).validators(),
            needsCheckbox: new FieldState<boolean>(
                consent?.needsCheckbox !== undefined ? consent.needsCheckbox : true
            ).validators(),
            showAfterAccept: new FieldState<boolean>(
                consent?.showAfterAccept !== undefined ? consent.showAfterAccept : true
            ).validators()
        });
    }

    public get fields() {
        return {
            consents: wrapInFormField(this.form.$.consents)
        };
    }

    public get errors() {
        return formErrors(this.form);
    }

    @bind
    public addConsent() {
        const newRule = this.createConsentForm();
        const currentRules = [...this.form.$.consents.value];
        currentRules.push(newRule);
        this.form.$.consents.onChange(currentRules);
        this.validateConsents();
    }

    @bind
    public removeConsent(index: number) {
        const currentRules = [...this.form.$.consents.value];
        if (index >= 0 && index < currentRules.length) {
            currentRules.splice(index, 1);
            this.form.$.consents.onChange(currentRules);
            this.validateConsents();
        }
    }

    public onCollapsedToggle() {
        this.isActive = !this.isActive;
    }

    public openStep() {
        this.isActive = true;
    }

    public closeStep() {
        this.isActive = false;
    }

    public enableConsents() {
        if (this.state === StepStatus.DISABLED) {
            this.validateConsents(false);
        }
    }

    public disableConsents() {
        this.state = StepStatus.DISABLED;
    }

    public generateRequestData(
        form: ChallengeConsentsFormType
    ): Consent[] {
        if (form.$.consents.value.length === 0 || this.state === StepStatus.DISABLED) {
            return [];
        }
        return form.$.consents.value.map((subForm) => {
            return {
                text: subForm.$.text.value,
                required: subForm.$.required.value,
                needsCheckbox: subForm.$.needsCheckbox.value,
                showAfterAccept: subForm.$.showAfterAccept.value
            };
        });
    }

    public get formIsDirty() {
        return (
            isFormStateDirty(this.form.$) ||
            this.form.$.consents.value.some((subForm) =>
                isFormStateDirty(subForm.$)
            )
        );
    }

    @bind
    public async validateConsents(onErrorOnly: boolean = false) {
        if (onErrorOnly) {
            if (this.state === StepStatus.ERROR) {
                const validation = await this.form.validate();
                const groupRulesValidation = await Promise.all(
                    this.form.$.consents.value.map((subForm) => subForm.validate())
                );
                return this.adjustState(validation.hasError || groupRulesValidation.some((v) => v.hasError));
            }
            this.state = StepStatus.MODIFIED;
            return false;
        } else {
            const validation = await this.form.validate();
            const groupRulesValidation = await Promise.all(
                this.form.$.consents.value.map((subForm) => subForm.validate())
            );
            return this.adjustState(validation.hasError || groupRulesValidation.some((v) => v.hasError));
        }
    }

    public adjustState(hasError: boolean) {
        if (hasError) {
            this.state = StepStatus.ERROR;
            return true;
        } else if (
            this.challengeDataStore.challengeConfiguration === undefined ||
            this.state === StepStatus.CREATING
        ) {
            this.state = StepStatus.CREATING;
            return false;
        } else if (this.formIsDirty) {
            this.state = StepStatus.MODIFIED;
            return false;
        }

        this.state = StepStatus.COMPLETED;
        makeFormClean(this.form);
        return false;
    }

    @bind
    public async onChangeHandle() {
        const validation = await this.form.validate();
        const groupRulesValidation = await Promise.all(
            this.form.$.consents.value.map((subForm) => subForm.validate())
        );

        if (
            validation.hasError ||
            groupRulesValidation.some((v) => v.hasError)
        ) {
            this.state = StepStatus.ERROR;
        } else {
            this.state = StepStatus.MODIFIED;
        }
    }

    @bind
    public async updateForm(consents: Consent[]) {
        this.form = this.generateForm(consents);
        const validation = await this.form.validate();
        const consentsValidation = await Promise.all(
            this.form.$.consents.value.map((subForm) => subForm.validate())
        );

        if (
            validation.hasError ||
            consentsValidation.some((v) => v.hasError)
        ) {
            this.state = StepStatus.ERROR;
            return;
        }
        this.state = StepStatus.COMPLETED;
        makeFormClean(this.form);
        this.form.$.consents.value.forEach((subForm) => makeFormClean(subForm));
        return;
    }

    @autodisposer.tsdi
    public initialize() {
        return [
            reaction(
                () => this.challengeDataStore.challengeConfiguration,
                async (configuration) => {
                    await this.updateForm(configuration?.joinConsents || []);
                }
            )
        ];
    }

    @bind
    public async updateConsents() {
        const validation = await this.validateConsents();

        if (validation) {
            this.state = StepStatus.ERROR;
        } else if (this.challengeDataStore.challengeId && this.formIsDirty) {
            this.state = StepStatus.UPDATING;
            if (
                this.challengeDataStore.challengeStatus ===
                ChallengeStatus.PUBLISHED
                || ChallengeStatus.IN_PROGRESS
            ) {
                await this.updateJoinConsents();
            }
        }
    }

    public updateJoinConsents() {
        return this.challengeJoinConsentRequest.request({
            challengeId: this.challengeDataStore.challengeId!,
            data: this.generateUpdateRequest(this.form)
        });
    }

    public generateUpdateRequest(
        form: ChallengeConsentsFormType
    ): ChallengeJoinConsentUpdateDto {
        return {
            joinConsents: this.generateRequestData(form)
        };
    }

    private get challengeClient() {
        return injectTSDI(Api).challengeClient;
    }

    public challengeJoinConsentRequest = wrapRequest(
        async ({
                   challengeId,
                   data
               }: {
            challengeId: number;
            data: ChallengeJoinConsentUpdateDto;
        }) => {
            const result =
                await this.challengeClient?.support.challengeSupportControllerUpdateJoinConsents(
                    challengeId,
                    data
                );

            return result?.data;
        }
    );
}
